import type { JSX } from 'react';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Layers, Wrench, Terminal, Globe, Trash2 } from 'lucide-react';
import type { EmbeddingProvider, SystemSettings } from '@echo-work-platform/shared-types';
import { api } from '@/lib/api-client';
import { KNOWLEDGE_BASE, isFeatureEnabled } from '@/auth/features';
import { Select } from '@/components/shared/select';
import { Tabs } from '@/components/shared/tabs';

type SettingsTab = 'embedding' | 'tool' | 'codeexec' | 'webtools';

interface SettingsResponse {
  settings: SystemSettings;
  frozen: boolean;
  chunkCount: number;
}

// Known dimensions for common models — prefilled, but editable.
const MODEL_DIMS: Record<string, number> = {
  'text-embedding-3-large': 3072,
  'text-embedding-3-small': 1536,
  'bge-m3': 1024,
};

export function SystemSettingsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['/api/admin/system-settings'],
    queryFn: () => api.get<SettingsResponse>('/api/admin/system-settings'),
  });

  // Defaults requested for the first rollout: Azure OpenAI text-embedding-3-large (3072).
  const [provider, setProvider] = useState<EmbeddingProvider>('AZURE_OPENAI');
  const [model, setModel] = useState('text-embedding-3-large');
  const [dimensions, setDimensions] = useState(3072);
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');

  // Tool-description chat model (not frozen). Empty provider = manual descriptions only.
  const [toolProvider, setToolProvider] = useState<EmbeddingProvider | ''>('');
  const [toolModel, setToolModel] = useState('');
  const [toolEndpoint, setToolEndpoint] = useState('');
  const [toolApiKey, setToolApiKey] = useState('');

  // Code-execution sandbox (global, not frozen). URL + the HMAC key every user's token derives from.
  const [codeExecUrl, setCodeExecUrl] = useState('');
  const [codeExecToken, setCodeExecToken] = useState('');

  const knowledgeBaseServed = isFeatureEnabled(KNOWLEDGE_BASE);
  const [tab, setTab] = useState<SettingsTab>(knowledgeBaseServed ? 'embedding' : 'codeexec');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [webSearchProvider, setWebSearchProvider] = useState<'exa' | 'parallel' | ''>('');
  const [webFetchEnabled, setWebFetchEnabled] = useState(true);
  const [blockedUrls, setBlockedUrls] = useState<string[]>([]);
  const [blockedDraft, setBlockedDraft] = useState('');

  // Hydrate the editable form fields from the server-fetched settings on load and
  // re-sync whenever they change (e.g. after a save-triggered refetch). This is an
  // intentional external-state → local-state sync, so the setState-in-effect rule is
  // scoped-off here; a keyed remount would not preserve the re-sync-on-refetch behavior.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const s = data?.settings;
    if (!s) return;
    if (s.embeddingProvider) {
      setProvider(s.embeddingProvider);
      setModel(s.embeddingModel ?? '');
      setDimensions(s.embeddingDimensions ?? 3072);
      setEndpoint(s.embeddingEndpoint ?? '');
    }
    if (s.toolDescProvider) setToolProvider(s.toolDescProvider);
    setToolModel(s.toolDescModel ?? '');
    setToolEndpoint(s.toolDescEndpoint ?? '');
    setCodeExecUrl(s.codeExecutionUrl ?? '');
    setWebSearchEnabled(s.webSearchEnabled);
    setWebSearchProvider(s.webSearchProvider ?? '');
    setWebFetchEnabled(s.webFetchEnabled);
    setBlockedUrls(s.webBlockedUrls ?? []);
  }, [data]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const save = useMutation({
    mutationFn: () =>
      api.patch<SystemSettings>('/api/admin/system-settings/embedding', {
        provider,
        model,
        dimensions,
        endpoint: endpoint || null,
        apiKey: apiKey || null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/system-settings'] }),
  });

  const saveTool = useMutation({
    mutationFn: () =>
      api.patch<SystemSettings>('/api/admin/system-settings/tool-description-model', {
        provider: toolProvider || null,
        model: toolModel || null,
        endpoint: toolEndpoint || null,
        apiKey: toolApiKey || null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/system-settings'] }),
  });

  const saveWebTools = useMutation({
    mutationFn: (urls: string[]) =>
      api.patch<SystemSettings>('/api/admin/system-settings/web-tools', {
        searchEnabled: webSearchEnabled,
        searchProvider: webSearchEnabled ? webSearchProvider || null : null,
        fetchEnabled: webFetchEnabled,
        blockedUrls: urls,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/system-settings'] });
    },
  });

  const blockedUrlsWithDraft = (list: string[]): string[] => {
    const value = blockedDraft.trim();
    return !value || list.includes(value) ? list : [...list, value];
  };

  const addBlockedDraft = () => {
    setBlockedUrls(blockedUrlsWithDraft);
    setBlockedDraft('');
  };

  const saveCodeExec = useMutation({
    mutationFn: () =>
      api.patch<SystemSettings>('/api/admin/system-settings/code-execution', {
        url: codeExecUrl || null,
        token: codeExecToken || null,
      }),
    onSuccess: () => {
      setCodeExecToken('');
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/system-settings'] });
    },
  });

  const frozen = data?.frozen ?? false;

  if (isLoading) return <p className="text-slate-500">Betöltés…</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-heading">Beállítások</h2>

      <Tabs
        value={tab}
        onChange={(v) => setTab(v as SettingsTab)}
        tabs={[
          ...(knowledgeBaseServed
            ? [
                { value: 'embedding', label: 'Beágyazási modell', icon: Layers },
                { value: 'tool', label: 'Tool-leírás modell', icon: Wrench },
              ]
            : []),
          { value: 'codeexec', label: 'Kódfuttató sandbox', icon: Terminal },
          { value: 'webtools', label: 'Webes eszközök', icon: Globe },
        ]}
      />

      {knowledgeBaseServed && tab === 'embedding' && (
        <div role="tabpanel" id="tabpanel-embedding" aria-labelledby="tab-embedding" className="space-y-4">
          {frozen && (
            <div className="card max-w-xl flex items-start gap-3 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                A beágyazási modell <strong>be van fagyasztva</strong>, mert már léteznek indexelt chunkok (
                {data?.chunkCount}). A modell csak akkor változtatható, ha előbb törlöd az összes tudásbázist
                (ez újraépíti a vektortér-oszlopot az új dimenzióval).
              </div>
            </div>
          )}

          <div className="card max-w-xl space-y-4 p-6">
            <h3 className="font-semibold text-heading">Beágyazási modell</h3>
            <div>
              <label className="label">Szolgáltató</label>
              <Select
                value={provider}
                disabled={frozen}
                onChange={(v) => setProvider(v as EmbeddingProvider)}
                options={[
                  { value: 'AZURE_OPENAI', label: 'Azure OpenAI' },
                  { value: 'OPENAI', label: 'OpenAI (felhő)' },
                  { value: 'LOCAL', label: 'Lokális (self-host)' },
                ]}
              />
            </div>

            <div>
              <label className="label">
                Modell {provider === 'AZURE_OPENAI' ? '(Azure deployment neve)' : ''}
              </label>
              <input
                className="input"
                value={model}
                disabled={frozen}
                onChange={(e) => {
                  const v = e.target.value;
                  setModel(v);
                  if (MODEL_DIMS[v]) setDimensions(MODEL_DIMS[v]);
                }}
                placeholder="text-embedding-3-large"
              />
            </div>

            <div>
              <label className="label">Dimenzió</label>
              <input
                className="input"
                type="number"
                value={dimensions}
                disabled={frozen}
                onChange={(e) => setDimensions(Number(e.target.value))}
              />
              <p className="mt-1 text-xs text-slate-500">
                text-embedding-3-large → 3072. A dimenzió meghatározza a vektoroszlopot, ezért befagyaszt.
              </p>
            </div>

            <div>
              <label className="label">Endpoint {provider === 'OPENAI' ? '(opcionális)' : ''}</label>
              <input
                className="input"
                value={endpoint}
                disabled={frozen}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder={
                  provider === 'AZURE_OPENAI'
                    ? 'https://<resource>.openai.azure.com'
                    : provider === 'LOCAL'
                      ? 'http://localhost:8080/v1'
                      : '(alapértelmezett OpenAI)'
                }
              />
            </div>

            <div>
              <label className="label">
                API kulcs{' '}
                {data?.settings.embeddingApiKeySecret ? '(beállítva — üresen hagyva változatlan)' : ''}
              </label>
              <input
                className="input"
                type="password"
                value={apiKey}
                disabled={frozen}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={provider === 'LOCAL' ? '(opcionális)' : '••••••'}
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                className="btn-primary"
                disabled={frozen || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Mentés…' : 'Mentés'}
              </button>
            </div>

            {save.isError && (
              <p className="flex items-center gap-2 text-sm text-rose-600">
                <AlertTriangle className="h-4 w-4" /> {String(save.error)}
              </p>
            )}
            {save.isSuccess && <p className="text-sm text-emerald-600">Mentve.</p>}
          </div>
        </div>
      )}

      {knowledgeBaseServed && tab === 'tool' && (
        <div
          role="tabpanel"
          id="tabpanel-tool"
          aria-labelledby="tab-tool"
          className="card max-w-xl space-y-4 p-6"
        >
          <div>
            <h3 className="font-semibold text-heading">Tool-leírás modell</h3>
            <p className="text-xs text-slate-500">
              A KB-k MCP tool-leírásának automatikus generálásához használt chat-modell. Ha nincs beállítva, a
              KB-knál csak kézi leírás adható meg. Bármikor módosítható.
            </p>
          </div>
          <div>
            <label className="label">Szolgáltató</label>
            <Select
              value={toolProvider}
              onChange={(v) => setToolProvider(v as EmbeddingProvider | '')}
              options={[
                { value: '', label: 'Nincs (csak kézi leírás)' },
                { value: 'AZURE_OPENAI', label: 'Azure OpenAI' },
                { value: 'OPENAI', label: 'OpenAI (felhő)' },
                { value: 'LOCAL', label: 'Lokális (self-host)' },
              ]}
            />
          </div>
          {toolProvider && (
            <>
              <div>
                <label className="label">
                  Modell {toolProvider === 'AZURE_OPENAI' ? '(Azure deployment neve)' : ''}
                </label>
                <input
                  className="input"
                  value={toolModel}
                  onChange={(e) => setToolModel(e.target.value)}
                  placeholder="gpt-5.4-mini"
                />
              </div>
              <div>
                <label className="label">Endpoint {toolProvider === 'OPENAI' ? '(opcionális)' : ''}</label>
                <input
                  className="input"
                  value={toolEndpoint}
                  onChange={(e) => setToolEndpoint(e.target.value)}
                  placeholder={
                    toolProvider === 'AZURE_OPENAI'
                      ? 'https://<resource>.openai.azure.com'
                      : toolProvider === 'LOCAL'
                        ? 'http://localhost:8080/v1'
                        : '(alapértelmezett OpenAI)'
                  }
                />
              </div>
              <div>
                <label className="label">
                  API kulcs{' '}
                  {data?.settings.toolDescApiKeySecret ? '(beállítva — üresen hagyva változatlan)' : ''}
                </label>
                <input
                  className="input"
                  type="password"
                  value={toolApiKey}
                  onChange={(e) => setToolApiKey(e.target.value)}
                  placeholder={toolProvider === 'LOCAL' ? '(opcionális)' : '••••••'}
                />
              </div>
            </>
          )}
          <div className="flex justify-end">
            <button className="btn-primary" disabled={saveTool.isPending} onClick={() => saveTool.mutate()}>
              {saveTool.isPending ? 'Mentés…' : 'Mentés'}
            </button>
          </div>
          {saveTool.isError && (
            <p className="flex items-center gap-2 text-sm text-rose-600">
              <AlertTriangle className="h-4 w-4" /> {String(saveTool.error)}
            </p>
          )}
          {saveTool.isSuccess && <p className="text-sm text-emerald-600">Mentve.</p>}
        </div>
      )}

      {tab === 'codeexec' && (
        <div
          role="tabpanel"
          id="tabpanel-codeexec"
          aria-labelledby="tab-codeexec"
          className="card max-w-xl space-y-4 p-6"
        >
          <div>
            <h3 className="font-semibold text-heading">Kódfuttató sandbox</h3>
            <p className="text-xs text-slate-500">
              A desktop code-executor plugin (python/node eszközök) ide küldi a kódot futtatásra. Globális
              beállítás — mentés után a desktop a következő lekérdezésnél felajánlja az újratöltést, és a
              sidecar megkapja az új URL-t/tokent. Ha üresen hagyod, a desktop a saját env-fájljára esik
              vissza.
            </p>
            <p className="text-xs text-amber-600">
              A HMAC-kulcs cseréje nem egy lépés: amíg a desktopok nem erősítették meg az újratöltést, a régi
              kulccsal jönnek. Ha egy lépésben írod át, minden hívás 401-et kap. A háromlépéses menet a{' '}
              <code>docs/konfiguraciok.md</code> sandbox-token bejegyzésében van.
            </p>
          </div>
          <div>
            <label className="label">Sandbox URL</label>
            <input
              className="input"
              value={codeExecUrl}
              onChange={(e) => setCodeExecUrl(e.target.value)}
              placeholder="https://sandbox.echowork.intuitech.studio"
            />
          </div>
          <div>
            <label className="label">
              HMAC-kulcs — ebből származik minden felhasználó tokenje{' '}
              {data?.settings.codeExecutionTokenSecret ? '(beállítva — üresen hagyva változatlan)' : ''}
            </label>
            <input
              className="input"
              type="password"
              value={codeExecToken}
              onChange={(e) => setCodeExecToken(e.target.value)}
              placeholder="••••••"
            />
          </div>
          <div className="flex justify-end">
            <button
              className="btn-primary"
              disabled={saveCodeExec.isPending}
              onClick={() => saveCodeExec.mutate()}
            >
              {saveCodeExec.isPending ? 'Mentés…' : 'Mentés'}
            </button>
          </div>
          {saveCodeExec.isError && (
            <p className="flex items-center gap-2 text-sm text-rose-600">
              <AlertTriangle className="h-4 w-4" /> {String(saveCodeExec.error)}
            </p>
          )}
          {saveCodeExec.isSuccess && <p className="text-sm text-emerald-600">Mentve.</p>}
        </div>
      )}

      {tab === 'webtools' && (
        <div role="tabpanel" id="tabpanel-webtools" aria-labelledby="tab-webtools" className="space-y-4">
          <div className="card max-w-xl space-y-4 p-6">
            <h3 className="font-semibold text-heading">Webes eszközök</h3>
            <p className="text-xs text-slate-500">
              A kulcsszavas webes keresés és a címről letöltés szervezeti szinten, minden felhasználóra
              egyszerre kapcsolható. Mentés után a desktop a következő lekérdezésnél felajánlja az
              újratöltést; a kapcsolás akkor lép életbe, amikor a felhasználó rábólint. Aki nemet mond, a
              következő bejelentkezésekor kapja meg.
            </p>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={webSearchEnabled}
                onChange={(e) => setWebSearchEnabled(e.target.checked)}
              />
              Kulcsszavas webes keresés (<code>websearch</code>)
            </label>
            {webSearchEnabled && (
              <div>
                <label className="label">Szolgáltató</label>
                <Select
                  value={webSearchProvider}
                  onChange={(v) => setWebSearchProvider(v as 'exa' | 'parallel' | '')}
                  options={[
                    { value: '', label: 'Válassz szolgáltatót…' },
                    { value: 'exa', label: 'Exa' },
                    { value: 'parallel', label: 'Parallel' },
                  ]}
                />
                <p className="mt-1 text-xs text-slate-500">
                  A szolgáltatói API-kulcs nem itt él: az a kliens env-fájljából jön, mert <code>secret</code>
                  . Kulcs nélkül a hívás is lefut, de szerződés nélkül.
                </p>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={webFetchEnabled}
                onChange={(e) => setWebFetchEnabled(e.target.checked)}
              />
              Letöltés ismert címről (<code>webfetch</code>)
            </label>

            <div>
              <label className="label">Blokkolt címek</label>
              <p className="mb-2 text-xs text-slate-500">
                Minták, nem teljes URL-ek. A <code>*.example.com</code> az aldomaineket fedi, az{' '}
                <code>example.com</code> a domaint és az aldomainjeit is, az <code>example.com/admin/*</code>{' '}
                pedig magát az <code>/admin</code> útvonalat és mindent alatta. Séma nélkül írd.
              </p>
              <ul className="mb-2 space-y-1">
                {blockedUrls.map((pattern) => (
                  <li key={pattern} className="flex items-center gap-2 text-sm">
                    <code className="grow">{pattern}</code>
                    <button
                      className="btn-ghost"
                      aria-label={`${pattern} törlése`}
                      onClick={() => setBlockedUrls((list) => list.filter((p) => p !== pattern))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
                {blockedUrls.length === 0 && <li className="text-sm text-slate-500">Nincs blokkolt cím.</li>}
              </ul>
              <div className="flex gap-2">
                <input
                  className="input grow"
                  value={blockedDraft}
                  onChange={(e) => setBlockedDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    addBlockedDraft();
                  }}
                  placeholder="example.com"
                />
                <button className="btn-secondary" disabled={!blockedDraft.trim()} onClick={addBlockedDraft}>
                  Hozzáadás
                </button>
              </div>
            </div>

            <p className="text-xs text-amber-600">
              A lista a platform eszközeit fogja: a letöltést visszatartja, a keresési találatokból pedig
              kiszűri a blokkolt domaineket. Nem hálózati tűzfal — a gép böngészőjét és a platformon kívüli
              forgalmat nem érinti. Kliensoldali, eszköz-szintű szabály, és átirányítást nem követ.
            </p>

            <div className="flex justify-end">
              <button
                className="btn-primary"
                disabled={saveWebTools.isPending || (webSearchEnabled && !webSearchProvider)}
                onClick={() => {
                  const urls = blockedUrlsWithDraft(blockedUrls);
                  setBlockedUrls(urls);
                  setBlockedDraft('');
                  saveWebTools.mutate(urls);
                }}
              >
                {saveWebTools.isPending ? 'Mentés…' : 'Mentés'}
              </button>
            </div>
            {saveWebTools.isError && (
              <p className="flex items-center gap-2 text-sm text-rose-600">
                <AlertTriangle className="h-4 w-4" /> {String(saveWebTools.error)}
              </p>
            )}
            {saveWebTools.isSuccess && <p className="text-sm text-emerald-600">Mentve.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
